import { useLayoutEffect, useSyncExternalStore } from 'react';
import { Desktop } from '../../design-system/icons';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { EmptyState } from './EmptyState';

export const DESKTOP_MIN_WIDTH = 1024;
const SMALL_SCREEN_QUERY = `(max-width: ${DESKTOP_MIN_WIDTH - 1}px)`;
const GATE_ATTR = 'data-small-screen-gate';

function subscribe(onChange: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(SMALL_SCREEN_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getSnapshot() {
  return typeof window !== 'undefined' && window.matchMedia?.(SMALL_SCREEN_QUERY).matches === true;
}

export function useIsSmallScreen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

// Surfaces that must stay usable on small screens (e.g. the Shopify install claim
// landing) hold the gate down here until they release it.
let exempt = false;
const exemptListeners = new Set<() => void>();

export function setSmallScreenGateExempt(value: boolean) {
  if (exempt === value) return;
  exempt = value;
  exemptListeners.forEach((fn) => fn());
}

export function useSmallScreenGateExempt(): boolean {
  return useSyncExternalStore(
    (fn) => {
      exemptListeners.add(fn);
      return () => exemptListeners.delete(fn);
    },
    () => exempt,
    () => false,
  );
}

const ownedByGate = (el: Element) => el.hasAttribute(GATE_ATTR) || !!el.querySelector(`[${GATE_ATTR}]`);

// Every body-level node except the gate's own goes inert + aria-hidden, including
// portals added later; the previous attributes are restored exactly on un-gate.
function useInertOutsideGate() {
  useLayoutEffect(() => {
    const saved = new Map<Element, { inert: boolean; ariaHidden: string | null }>();
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const apply = () => {
      for (const el of Array.from(document.body.children)) {
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
        if (ownedByGate(el)) {
          if (el.hasAttribute('aria-hidden')) el.removeAttribute('aria-hidden');
          if (el.hasAttribute('inert')) el.removeAttribute('inert');
          continue;
        }
        if (!saved.has(el)) saved.set(el, { inert: el.hasAttribute('inert'), ariaHidden: el.getAttribute('aria-hidden') });
        if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
        if (el.getAttribute('aria-hidden') !== 'true') el.setAttribute('aria-hidden', 'true');
      }
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-hidden', 'inert'],
    });
    return () => {
      observer.disconnect();
      saved.forEach((prev, el) => {
        if (!prev.inert) el.removeAttribute('inert');
        if (prev.ariaHidden === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', prev.ariaHidden);
      });
      // After the dialog's own teardown, which would otherwise drop focus on body.
      setTimeout(() => {
        if (previouslyFocused?.isConnected) previouslyFocused.focus();
      }, 0);
    };
  }, []);
}

export function SmallScreenGate({ onSignOut }: { onSignOut?: () => void }) {
  useInertOutsideGate();

  return (
    <Dialog open modal>
      <DialogContent
        showCloseButton={false}
        aria-modal="true"
        {...{ [GATE_ATTR]: '' }}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="top-0 left-0 h-dvh max-w-none translate-0 content-center rounded-none border-0 sm:max-w-none"
      >
        <EmptyState
          icon={Desktop}
          title="Discovr works best on a larger screen"
          body="Open Discovr on a laptop or desktop, or widen this window, to continue."
          titleAs={DialogTitle}
          bodyAs={DialogDescription}
          className="py-0"
        />
        {onSignOut && (
          <Button variant="outline" size="sm" onClick={onSignOut} className="mx-auto">
            Sign out
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
